import { useTranslation } from "react-i18next"
import {
  ArrowRight,
  Brain,
  CaretDown,
  Check,
  EyedropperSample,
  FileText,
  Prescription,
  SuitcaseSimple,
  TestTube,
} from "@phosphor-icons/react"
import { Button } from "@/components/ui/button"
import { Chip } from "@/components/ui/chip"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { cn } from "@/lib/utils"
import { RadiologyIcon } from "@/modules/patients/detail/progress-note/documents/meas-radiology-prescription-document"
import { NutritionIcon } from "@/modules/patients/detail/progress-note/documents/nutritional-counselling-document"
import { PsychologicalPsychotherapyIcon } from "@/modules/patients/detail/progress-note/documents/psychological-psychotherapy"
import { PhysicalTherapyIcon } from "@/modules/patients/detail/progress-note/documents/therapy-prescription-document"
import {
  useAllProgressNotesFilters,
  useEpisodeOfCareStoreContext,
} from "@/modules/patients/detail/store/useEpisodeOfCareStore"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { AllCountriesFilterableProgressNoteDocument } from "@/modules/patients/types/ProgressNoteDocument"

const useProgressNoteDropdownItemContent = (
  documentType: keyof AllCountriesFilterableProgressNoteDocument,
  isSelected?: boolean,
) => {
  const { t } = useTranslation()
  const countryCode = useCountryParam()

  switch (documentType) {
    case "prescription":
      return (
        <>
          <Prescription className={isSelected ? "text-on-primary-surface" : "text-on-surface-subdued"} />
          {t("patient.prescription.prescription")}
        </>
      )
    case "sickLeave":
      return (
        <>
          <SuitcaseSimple className={isSelected ? "text-on-primary-surface" : "text-on-surface-subdued"} />
          {countryCode === "CH"
            ? t("episodeOfCare.progressNote.sickLeaveCertificate.ch")
            : t("episodeOfCare.progressNote.sickLeaveCertificate.de")}
        </>
      )
    case "therapyPrescription":
      return (
        <>
          <PhysicalTherapyIcon isSelected={isSelected} />
          {t("episodeOfCare.progressNote.deTherapyPrescription")}
        </>
      )
    case "attestationGuardian":
      return (
        <>
          <FileText className={isSelected ? "text-on-primary-surface" : "text-on-surface-subdued"} />
          {t("episodeOfCare.progressNote.attestationForLegalGuardian")}
        </>
      )
    case "physiotherapy":
      return (
        <>
          <PhysicalTherapyIcon isSelected={isSelected} />
          {t("patient.therapyPrescriptionPhysiotherapy.therapyPrescriptionPhysiotherapy")}
        </>
      )
    case "ch_nutritionalPrescription":
      return (
        <>
          <NutritionIcon isSelected={isSelected} />
          {t("episodeOfCare.progressNote.therapyPrescriptionNutritionalCounselling")}
        </>
      )
    case "psychologicalPsychotherapy":
      return (
        <>
          <PsychologicalPsychotherapyIcon isSelected={isSelected} />
          {t("patient.therapyPrescription.therapyPrescriptionPsychologicalPsychotherapy")}
        </>
      )
    case "generalAttestation":
      return (
        <>
          <FileText className={isSelected ? "text-on-primary-surface" : "text-on-surface-subdued"} />
          {t("episodeOfCare.progressNote.generalAttestation")}
        </>
      )
    case "referralLetter":
      return (
        <>
          <ArrowRight className={isSelected ? "text-on-primary-surface" : "text-on-surface-subdued"} />
          {t("episodeOfCare.progressNote.referral")}
        </>
      )
    case "ch_measRadiologyPrescription":
      return (
        <>
          <RadiologyIcon isSelected={isSelected} />
          {t("patient.measRadiologyPrescription.radiologyPrescriptionMEAS")}
        </>
      )
    case "measLaboratoryPrescription":
      return (
        <>
          <TestTube className={isSelected ? "text-on-primary-surface" : "text-on-surface-subdued"} />
          {t("patient.measLaboratoryPrescription.measLaboratoryPrescription")}
        </>
      )
    case "medicalReportAttachments":
      return (
        <>
          <FileText className={isSelected ? "text-on-primary-surface" : "text-on-surface-subdued"} />
          {t("episodeOfCare.progressNote.medicalReport")}
        </>
      )
    case "carePlanSummary":
      return (
        <>
          <FileText className={isSelected ? "text-on-primary-surface" : "text-on-surface-subdued"} />
          {t("episodeOfCare.progressNote.treatmentPlan.headerTitle")}
        </>
      )
    case "ch_neuropsychologicDiagnosticPrescription":
      return (
        <>
          <Brain className={isSelected ? "text-on-primary-surface" : "text-on-surface-subdued"} />
          {t("patient.neuropsychologicDiagnosticPrescription.neuropsychologicDiagnosticPrescription")}
        </>
      )
    case "ch_laboratoryPrescription":
      return (
        <>
          <EyedropperSample className={isSelected ? "text-on-primary-surface" : "text-on-surface-subdued"} />
          {t("patient.laboratoryPrescription.prescriptionForMedgateConnect")}
        </>
      )
    case "ch_consultationReport":
      return (
        <>
          <FileText className={isSelected ? "text-on-primary-surface" : "text-on-surface-subdued"} />
          {t("patient.consultationReport.title")}
        </>
      )
    default:
      return null
  }
}

const ProgressNotesDropdownItem = ({
  documentType,
}: {
  documentType: keyof AllCountriesFilterableProgressNoteDocument
}) => {
  const { filters } = useAllProgressNotesFilters()
  const dropdownContent = useProgressNoteDropdownItemContent(documentType, filters[documentType])
  const setFilters = useEpisodeOfCareStoreContext((state) => state.setAllProgressNotesFilters)

  return (
    <DropdownMenuCheckboxItem
      onSelect={(e) => e.preventDefault()}
      key={documentType}
      data-testid={`progress-notes-filter-${documentType}`}
      className="gap-2.5 leading-5"
      onCheckedChange={() => setFilters({ [documentType]: !filters[documentType] })}
      checked={filters[documentType]}>
      {dropdownContent}
    </DropdownMenuCheckboxItem>
  )
}

const ProgressNotesFilter = ({
  documentTypes,
}: {
  documentTypes: Array<keyof AllCountriesFilterableProgressNoteDocument>
}) => {
  const { t } = useTranslation()
  const resetDocumentFilters = useEpisodeOfCareStoreContext((state) => state.resetDocumentFilters)
  const { filters, hasDocumentFilters } = useAllProgressNotesFilters()
  const activeFilterCount = documentTypes.filter((documentType) => filters[documentType]).length

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger
        asChild
        className={cn(
          "aria-checked:border-on-primary-surface aria-checked:bg-primary-surface aria-checked:text-on-primary-surface aria-checked:hover:enabled:bg-primary-surface-hovered aria-checked:focus:enabled:border-on-primary-surface outline-hidden",
        )}>
        <Chip
          data-testid="progress-notes-filter-trigger"
          className="h-8 gap-0.5"
          checked={hasDocumentFilters}
          type="button">
          <div className={`overflow-hidden transition-all duration-200 ${hasDocumentFilters ? "w-5" : "w-0"}`}>
            <Check size={20} />
          </div>
          <span className="px-1.5">
            {t("episodeOfCare.filters.documents")} {activeFilterCount > 0 && `(${activeFilterCount})`}
          </span>
          <CaretDown size={20} />
        </Chip>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="bottom"
        align="start"
        data-testid="progress-notes-filter-content"
        className="absolute -top-1 grid gap-2 rounded-md border-none p-2 leading-5">
        {documentTypes.length > 0 ? (
          <>
            <div className="grid gap-0.5">
              {documentTypes.map((documentType) => (
                <ProgressNotesDropdownItem documentType={documentType} key={documentType} />
              ))}
            </div>
            <DropdownMenuSeparator className="bg-border -mx-2 my-0" />
            <div className="flex justify-end">
              <Button
                onClick={resetDocumentFilters}
                data-testid="reset-progress-notes-filter"
                variant="tertiary"
                className="h-9 rounded-full px-2 py-0 leading-5 font-medium">
                {t("common.button.clear")}
              </Button>
            </div>
          </>
        ) : (
          <div className="p-2 text-center text-[14px] leading-5">{t("episodeOfCare.filters.noDocuments")}</div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export { ProgressNotesFilter }
